'use client'

import { useProductListAnalyticsProvider } from 'product-list/context/ProductListAnalyticsProvider/useProductListAnalyticsProvider'
import {
	createProductListStore,
	type ProductListStore,
	type ProductListStoreApi,
	type ProductListStoreParams,
} from 'product-list/stores/productListStore'
import type { ProductListViewRef } from 'product-list/types/ProductsList'
import type { View } from 'product-list/types/View'
import {
	createContext,
	forwardRef,
	type ReactNode,
	useImperativeHandle,
	useMemo,
	useRef,
} from 'react'
import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

interface ProductListProviderProps extends ProductListStoreParams {
	readonly children: ReactNode
	readonly onViewChange?: (view: View) => void
}

interface ProductListContextType {
	productListStore: ProductListStoreApi
}

export const ProductListContext = createContext<ProductListContextType | null>(
	null
)

export const ProductListProvider = forwardRef<
	ProductListViewRef,
	Readonly<ProductListProviderProps>
>((params, ref) => {
	const { children, onViewChange, ...storeParams } = params
	const { onListEndWasReached } = useProductListAnalyticsProvider()

	const storeRef = useRef<ProductListStoreApi | null>(null)
	const listEndSelector = useShallow((state: ProductListStore) => ({
		count: state.count,
		total: state.total,
	}))

	if (storeRef.current === null) {
		storeRef.current = createProductListStore(storeParams)

		storeRef.current.subscribe(
			(state) => state.view,
			(view) => onViewChange?.(view)
		)

		storeRef.current.subscribe(listEndSelector, ({ count, total }) => {
			if (count >= total) {
				onListEndWasReached?.()
			}
		})
	}

	const productListStore = storeRef.current

	const value = useMemo(() => ({ productListStore }), [productListStore])

	const setView = useStore(productListStore, (state) => state.setView)
	useImperativeHandle(ref, () => ({ setView }), [setView])

	return (
		<ProductListContext.Provider value={value}>
			{children}
		</ProductListContext.Provider>
	)
})
